import type { RouteContext } from '../router';
import { renderShell } from '../ui/shell';
import { el, clear } from '../ui/dom';
import { buildTestCases, buildAamvaData } from './barcode-data';
import type { AamvaTestCase } from './barcode-data';
import type { Pdf417BarcodeArray } from '../vendor/pdf417';

// The vendor lib is a classic (non-module) script that assigns `window.PDF417`
// as a side effect. Importing it with `?raw` keeps the file byte-for-byte
// untouched on disk; we inject it once as an inline <script> so it executes
// in global scope exactly like the Angular app's `angular.json`-loaded copy.
import pdf417Source from '../vendor/pdf417.js?raw';

let pdf417Ready: Promise<void> | null = null;

function ensurePdf417Loaded(): Promise<void> {
  if (!pdf417Ready) {
    pdf417Ready = new Promise((resolve) => {
      const script = document.createElement('script');
      script.textContent = pdf417Source;
      document.head.appendChild(script);
      resolve();
    });
  }
  return pdf417Ready;
}

export async function renderBarcodes({ root }: RouteContext): Promise<void> {
  const content = renderShell(root, 'barcodes');
  await ensurePdf417Loaded();

  const testCases = buildTestCases();
  let currentIndex = 0;

  const body = el('div', {
    style: 'display:flex; gap:20px; align-items:flex-start;',
  });
  content.append(
    el('div', { class: 'view-head' }, [
      el('div', { class: 'page-head' }, [el('h1', { textContent: 'AAMVA Test Barcodes' })]),
    ]),
    el('p', { class: 'view-caption' }, [
      'Sample PDF417 driver-license barcodes for exercising the scanner and ',
      'validator — pick a sample or use ← / → to step through all 65.',
    ]),
    body,
  );

  const sidebar = el('div', {
    class: 'panel',
    style: 'width:280px; flex-shrink:0; max-height:76vh; overflow-y:auto;',
  });
  const detail = el('div', { class: 'panel', style: 'flex:1; min-width:0;' });
  body.append(sidebar, detail);

  function select(index: number): void {
    currentIndex = Math.max(0, Math.min(testCases.length - 1, index));
    renderSidebar(sidebar, testCases, currentIndex, select);
    renderDetail(detail, testCases, currentIndex, select);
  }

  select(0);

  // Self-cleaning keyboard nav: bails out and unhooks the first time this
  // view is no longer on screen (the router just clears `root` on
  // navigation, there is no explicit unmount hook to use instead).
  const onKeydown = (e: KeyboardEvent): void => {
    if (!body.isConnected) {
      window.removeEventListener('keydown', onKeydown);
      return;
    }
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      select(currentIndex + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      select(currentIndex - 1);
    }
  };
  window.addEventListener('keydown', onKeydown);
}

function renderSidebar(
  sidebar: HTMLElement,
  testCases: AamvaTestCase[],
  currentIndex: number,
  select: (index: number) => void,
): void {
  clear(sidebar);
  const categories = [...new Set(testCases.map((t) => t.category))];

  const items: HTMLElement[] = [];
  for (const category of categories) {
    items.push(
      el('div', {
        class: 'panel-subhead',
        style: 'margin-top:14px;',
        textContent: category,
      }),
    );
    testCases.forEach((test, index) => {
      if (test.category !== category) return;
      const active = index === currentIndex;
      items.push(
        el(
          'button',
          {
            class: `nav-link${active ? ' nav-on' : ''}`,
            style: 'display:block; width:100%; text-align:left; font-size:13px;',
            type: 'button',
            onClick: () => select(index),
          },
          [test.title.replace(/^Test \d+:\s*/, '')],
        ),
      );
    });
  }
  sidebar.append(...items);
}

function renderDetail(
  detail: HTMLElement,
  testCases: AamvaTestCase[],
  currentIndex: number,
  select: (index: number) => void,
): void {
  clear(detail);
  const test = testCases[currentIndex];
  const aamvaData = buildAamvaData(test);

  const prevBtn = el(
    'button',
    {
      class: 'btn btn-ghost btn-sm',
      type: 'button',
      disabled: currentIndex === 0,
      onClick: () => select(currentIndex - 1),
    },
    ['‹ Prev'],
  );
  const nextBtn = el(
    'button',
    {
      class: 'btn btn-ghost btn-sm',
      type: 'button',
      disabled: currentIndex === testCases.length - 1,
      onClick: () => select(currentIndex + 1),
    },
    ['Next ›'],
  );

  const head = el('div', { class: 'panel-head' }, [
    el('div', {}, [
      el('h2', { class: 'panel-title', textContent: test.title }),
      el('p', { class: 'panel-sub', textContent: test.description }),
    ]),
    el('div', { class: 'button-row' }, [
      el('span', {
        class: 'cell-muted',
        style: 'align-self:center; font-size:12px;',
        textContent: `Test ${currentIndex + 1} of ${testCases.length}`,
      }),
      prevBtn,
      nextBtn,
    ]),
  ]);

  const expectedPill = el('span', {
    class: `pill ${test.expected.type === 'valid' ? 'pill-ok' : 'pill-warn'}`,
    textContent: test.expected.status,
  });
  const expected = el('div', { style: 'display:flex; flex-direction:column; gap:4px;' }, [
    el('div', {}, [
      el('span', {
        class: 'panel-subhead',
        style: 'margin-top:0;',
        textContent: 'Category: ',
      }),
      el('span', { class: 'cell-muted', textContent: test.category }),
    ]),
    el('div', {}, [expectedPill]),
    el('p', { class: 'panel-note', textContent: test.expected.notes }),
  ]);

  const canvas = el('canvas', {
    style: 'display:block; max-width:100%; image-rendering:pixelated;',
  });
  const canvasHost = el(
    'div',
    {
      style:
        'overflow-x:auto; background:#fff; padding:16px; border-radius:8px; ' +
        'border:1px solid var(--border);',
    },
    [canvas],
  );
  renderBarcodeOnCanvas(canvas, aamvaData);

  const copyBtn = el('button', { class: 'btn btn-ghost btn-sm', type: 'button' }, ['Copy']);
  copyBtn.addEventListener('click', () => {
    void navigator.clipboard.writeText(aamvaData).then(
      () => {
        copyBtn.textContent = 'Copied!';
        setTimeout(() => (copyBtn.textContent = 'Copy'), 1500);
      },
      () => {
        copyBtn.textContent = 'Copy failed';
        setTimeout(() => (copyBtn.textContent = 'Copy'), 1500);
      },
    );
  });

  const rawSection = el('div', {}, [
    el('div', { class: 'section-head', style: 'margin:16px 0 8px;' }, [
      el('h2', { textContent: 'Raw data' }),
      copyBtn,
    ]),
    el(
      'pre',
      {
        style:
          'white-space:pre-wrap; word-break:break-all; font-family:ui-monospace,' +
          'SFMono-Regular,Menlo,monospace; font-size:12px; background:var(--bg); ' +
          'padding:12px; border-radius:8px; max-height:220px; overflow:auto; margin:0;',
        textContent: aamvaData,
      },
      [],
    ),
  ]);

  detail.append(head, expected, canvasHost, rawSection);
}

function renderBarcodeOnCanvas(canvas: HTMLCanvasElement, aamvaData: string): void {
  const pdf417 = window.PDF417;
  if (!pdf417) return;

  pdf417.init(aamvaData);
  const barcode: Pdf417BarcodeArray = pdf417.getBarcodeArray();

  let blackCount = 0;
  for (let r = 0; r < barcode.num_rows; r++) {
    for (let c = 0; c < barcode.num_cols; c++) {
      if (Number(barcode.bcode[r][c]) === 1) blackCount++;
    }
  }
  if (blackCount === 0) return;

  const scale = 3;
  canvas.width = barcode.num_cols * scale;
  canvas.height = barcode.num_rows * scale;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = '#000000';
  for (let r = 0; r < barcode.num_rows; r++) {
    for (let c = 0; c < barcode.num_cols; c++) {
      if (Number(barcode.bcode[r][c]) === 1) {
        ctx.fillRect(c * scale, r * scale, scale, scale);
      }
    }
  }
}
